import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useSEO } from '@/hooks/useSEO';
import { 
  Brain, 
  Zap, 
  Cloud, 
  FileText, 
  MessageSquare, 
  Smartphone,
  Shield,
  Server,
  TrendingUp,
  Users,
  Search,
  BarChart3,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import TrainingPrograms from '@/components/TrainingPrograms';

const Services = () => {
  const { t } = useLanguage();
  
  // Set SEO for services page highlighting free assessment
  useSEO({
    title: "Digital Transformation Services | FREE Assessment Included | QuantumBox AI",
    description: "Comprehensive digital transformation services with FREE assessment. AI automation, process optimization, cloud migration, cybersecurity, and more. Get your free digital readiness evaluation and strategic roadmap.",
    keywords: "digital transformation services, AI automation services, process optimization, cloud migration, cybersecurity services, free digital assessment, business automation, workflow optimization",
    ogTitle: "Digital Transformation Services + FREE Assessment | QuantumBox AI",
    ogDescription: "Professional digital transformation services with complimentary assessment. AI automation, process optimization, and strategic consulting. Start with your free evaluation today!",
    canonical: "https://quantumbox.ai/services"
  });
  
  const services = [
    {
      icon: TrendingUp,
      title: t('services.digital.title'),
      description: t('services.digital.desc'),
      features: ['Legacy System Modernization', 'Process Digitization', 'Change Management', 'Digital Strategy Consulting'],
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      icon: Brain,
      title: t('services.ai.title'),
      description: t('services.ai.desc'),
      features: ['Machine Learning Models', 'Predictive Analytics', 'Process Automation', 'Intelligent Decision Making'],
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      icon: Zap,
      title: t('services.workflow.title'),
      description: t('services.workflow.desc'),
      features: ['Process Analysis', 'Bottleneck Identification', 'Automation Implementation', 'Performance Monitoring'],
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      icon: FileText,
      title: t('services.document.title'),
      description: t('services.document.desc'),
      features: ['OCR & Text Extraction', 'Document Classification', 'Data Processing', 'Workflow Integration'],
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      icon: Search,
      title: t('services.model.title'),
      description: t('services.model.desc'),
      features: ['Custom LLM Development', 'Domain-Specific Training', 'Model Fine-tuning', 'Private Deployment'],
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      icon: MessageSquare,
      title: t('services.chatbot.title'),
      description: t('services.chatbot.desc'),
      features: ['RAG Implementation', 'Contextual Understanding', 'Multi-language Support', 'Integration APIs'],
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      icon: Smartphone,
      title: t('services.app.title'),
      description: t('services.app.desc'),
      features: ['React & React Native', 'Progressive Web Apps', 'API Development', 'Cloud Integration'],
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      icon: Users,
      title: t('services.consulting.title'),
      description: t('services.consulting.desc'),
      features: ['Strategic Planning', 'Technology Assessment', 'Implementation Roadmap', 'Change Management'],
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      icon: Cloud,
      title: t('services.cloud.title'),
      description: t('services.cloud.desc'),
      features: ['Cloud Migration', 'Infrastructure Setup', 'DevOps Implementation', 'Monitoring & Maintenance'],
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      icon: Shield,
      title: t('services.security.title'),
      description: t('services.security.desc'),
      features: ['Security Audits', 'Threat Detection', 'Data Encryption', 'Compliance Management'],
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      icon: Server,
      title: t('services.server.title'),
      description: t('services.server.desc'),
      features: ['Server Installation', 'Network Configuration', 'Maintenance Services', 'Performance Optimization'],
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      icon: BarChart3,
      title: t('services.research.title'),
      description: t('services.research.desc'),
      features: ['Industry Research', 'Innovation Labs', 'Proof of Concepts', 'Technology Evaluation'],
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      icon: Users,
      title: t('services.training.title'),
      description: t('services.training.description'),
      features: ['L1-L5 Certification Levels', 'Online & On-site Training', '15-20 Hour Programs', 'Industry-Specific Modules'],
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    }
  ];

  const useCases = [
    {
      industry: 'Healthcare',
      title: 'AI-Driven Diagnostic Assistance',
      description: 'Reducing diagnostic errors by 40% and operational costs by 30% through intelligent medical imaging analysis.',
      metrics: ['40% Error Reduction', '30% Cost Savings', '60% Faster Diagnosis']
    },
    {
      industry: 'Government',
      title: 'Smart City Traffic Management',
      description: 'Improving commute times by 25% and reducing emissions by 20% with AI-powered traffic optimization.',
      metrics: ['25% Faster Commutes', '20% Emission Reduction', '50% Better Flow']
    },
    {
      industry: 'Finance',
      title: 'Fraud Detection & Prevention',
      description: 'Saving millions annually through advanced machine learning fraud detection systems.',
      metrics: ['99.5% Accuracy', 'Real-time Detection', 'Millions Saved']
    },
    {
      industry: 'Insurance',
      title: 'Automated Claims Processing',
      description: 'Enhancing customer satisfaction by 45% through intelligent claims automation and processing.',
      metrics: ['45% Higher Satisfaction', '70% Faster Processing', '90% Automation']
    },
    {
      industry: 'Logistics',
      title: 'AI-Powered Supply Chain',
      description: 'Improving delivery times by 35% and reducing costs by 25% through intelligent logistics optimization.',
      metrics: ['35% Faster Delivery', '25% Cost Reduction', '95% Accuracy']
    },
    {
      industry: 'Mining & Exploration',
      title: 'Sovereign Scout — Satellite Mineral Intelligence',
      description: 'Cutting greenfield exploration time by 70% through hyperspectral satellite imagery, geological AI, and sovereign data sovereignty for critical minerals discovery.',
      metrics: ['70% Faster Discovery', '85% Target Accuracy', '10× Coverage Area']
    },
    {
      industry: 'Natural Resources',
      title: 'Geoscience AI for Energy & Water',
      description: 'Predictive modeling for oil & gas reservoirs, lithium brines, groundwater aquifers, and rare-earth deposits — combining remote sensing, geophysics, and deep learning.',
      metrics: ['60% Less Drilling Risk', '40% CAPEX Reduction', '92% Model Precision']
    },
    {
      industry: 'Legal & Finance',
      title: 'Intelligent Document Automation',
      description: 'Extracting, classifying and validating data from complex contracts, mining permits, regulatory filings and 1000+ page reports with multimodal LLMs and human-in-the-loop review.',
      metrics: ['95% Extraction Accuracy', '80% Time Savings', '100% Audit Trail']
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-subtle">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('services.title')}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="group hover:shadow-elegant transition-smooth h-full">
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className={`p-3 ${service.bgColor} rounded-lg`}>
                        <IconComponent className={`h-6 w-6 ${service.color}`} />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </div>
                    <CardDescription className="text-base leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('services.impact.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('services.impact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <Card key={index} className="hover:shadow-card transition-smooth">
                <CardHeader>
                  <Badge variant="outline" className="w-fit mb-2">{useCase.industry}</Badge>
                  <CardTitle className="text-xl">{useCase.title}</CardTitle>
                  <CardDescription className="text-base">
                    {useCase.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-2">
                    {useCase.metrics.map((metric, metricIndex) => (
                      <div key={metricIndex} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-primary rounded-full" />
                        <span className="text-sm font-medium text-primary">{metric}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Training Programs Section */}
      <TrainingPrograms />

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('services.cta.title')}
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            {t('services.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Link to="/contact">
              <Button variant="hero" size="lg">
                {t('services.cta.consultation')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" size="lg">
                {t('services.cta.learn')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;